/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  CheckboxOnChangeData,
  Dropdown,
  Field as FluentField,
  Input,
  InputOnChangeData,
  Label,
  makeStyles,
  Option,
  Spinner,
  Switch,
} from "@fluentui/react-components";
import { ChevronDownFilled, ChevronRightFilled } from "@fluentui/react-icons";
import { useMsal } from "@skanska/vsa-client-auth";
import { FluentPeoplePicker } from "@skanska/vsa-people-picker";
import {
  ChangeEvent,
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  KmaChapterFieldUniqueName,
  PublishAsGeneratedPdfFieldUniqueName,
} from "../../constants/Fields";
import {
  canPublishAsGeneratedPdf,
  normalizePublishAsGeneratedPdfValue,
} from "../../constants/PublishAsGeneratedPdf";
import { useChaptersGetAll } from "../../hooks/useChapter";
import { useGraphClient } from "../../hooks/useGraphClient";
import {
  useCancelToolDraft,
  useGetCurrentPublishedDocumentsNames,
} from "../../hooks/useTools";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Chapter } from "../../models/Chapter";
import { Field, FieldValue } from "../../models/Field";
import { FieldTypes, NumFieldType } from "../../models/FieldTypes";
import { Tool, ToolFormData, ToolTypes } from "../../models/Tool";
import { UserInformation } from "../../models/UserInformation";
import { FlatVsaaTag, VsaaTag } from "../../models/VsaaTag";
import {
  isStringAUrl,
  validateFormFieldsForRequest,
} from "../../services/FieldService";
import { getFileExtension, getToolFileName } from "../../services/ToolService";
import { VsaaColorTokens } from "../../style/VsaaColorTokens";
import { SkanskaColorTokens } from "../../style/colors";
import { CheckoutMessageBar } from "../CheckoutMessageBar";
import { DisplayField } from "../DisplayField";
import { StickyTopBannerStack } from "../StickyTopBannerStack";
import { SaveSplitButton } from "../SaveSplitButton";
import { VsaaTagPicker } from "../tagpicker/VsaaTagPicker";
import { CancelToolDraftDialog } from "./CancelToolDraftDialog";
import FileSelection from "./FileSelect";
import ToolLink from "./ToolLink";
import ToolPublishDialog from "./ToolPublishDialog";
import { useGetUserAccessLevel } from "../../hooks/useGetUserAccessLevel";

const useClasses = makeStyles({
  root: {
    gap: "10px",
    display: "flex",
    flexDirection: "column",
    paddingLeft: "5px",
    marginBottom: "20px",
  },
  newToolContainerPrompt: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    gap: "10px",
  },
  textFieldWidth: {
    width: "400px",
    "& .ms-TextField-field": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      resize: "horizontal",
    },
    "& .ms-TextField-field:focus": {
      whiteSpace: "normal",
      wordWrap: "break-word",
    },
  },
  newToolContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  button: {
    marginTop: "10px",
  },
  fileSelectionContainer: {
    marginTop: "10px",
  },
  toggleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "12px",
  },
  formSpacingContainer: {
    margin: "20px 0",
    maxWidth: "450px",
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
  },
  actionButtons: {
    marginTop: "30px",
    display: "flex",
    marginLeft: "125px",
    gap: "10px",
  },
  existingToolInformation: {
    display: "flex",
    gap: "10px",
    flexDirection: "column",
    marginTop: "10px",
  },
  confirmDeleteButton: {
    backgroundColor: SkanskaColorTokens.color_tertiary_red_30,
    ":hover, :focus, :active": {
      backgroundColor: SkanskaColorTokens.color_tertiary_red,
      color: SkanskaColorTokens.color_secondary_white,
    },
  },
  peoplePickerContainer: {
    margin: "5px 0",
    maxWidth: "450px",
  },
  toolStatusContainer: {
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "500px",
    margin: "10px 0",
  },
  accordionHeader: {
    borderBottom: "1px solid #DDD",
    maxWidth: "500px",
  },
  deleteButton: {
    marginTop: "15px",
    display: "flex",
    marginLeft: "175px",
    gap: "10px",
  },
  warningText: {
    color: SkanskaColorTokens.color_action_red,
  },
  userFieldReadOnlyWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  fileNameInputContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  fileExtensionBadge: {
    color: VsaaColorTokens.color_file_extension_badge_text,
    backgroundColor: VsaaColorTokens.color_file_extension_badge_background,
    border: `1px solid ${VsaaColorTokens.color_file_extension_badge_border}`,
    borderRadius: "4px",
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    userSelect: "none",
    pointerEvents: "none",
  },
});

interface ToolFormProps {
  existingTool?: Tool | undefined;
  toolFields?: Field[];
  onSubmit: (
    data: ToolFormData,
    options?: { publishAfterSave?: boolean }
  ) => void | Promise<void>;
  isLoading: boolean;
  isRefetching: boolean;
  defaultToolType?: ToolTypes;
  onToolTypeChange?: (toolType: ToolTypes) => void;
  /** When true, automatically opens the publish dialog once the tool has loaded (used after a combined save-and-publish create). */
  autoOpenPublishDialog?: boolean;
}

const hasKmaVariantTag = (chapter: Chapter): boolean => {
  return Boolean(chapter.KMACollaborationVariant?.id);
};

const getUserIds = (
  userInfo: UserInformation[] | string[] | string | null | undefined
): string[] => {
  if (!userInfo) return [];
  if (Array.isArray(userInfo)) {
    if (typeof userInfo[0] === "string") {
      return userInfo as string[];
    }
    return (userInfo as UserInformation[])
      .map((u) => u.id)
      .filter(Boolean) as string[];
  }
  if (typeof userInfo === "string") return [userInfo];
  return [];
};

const getToolType = (
  existingTool: Tool | undefined,
  defaultToolType: ToolTypes | undefined
): string | undefined => {
  if (!existingTool) {
    return defaultToolType;
  }

  return existingTool.fileUniqueId ? ToolTypes.Document : ToolTypes.Link;
};

const getInitialFields = (
  existingTool: Tool | undefined
): Record<string, FieldValue> => {
  if (!existingTool?.fieldValues) {
    return {};
  }

  return {
    ...existingTool.fieldValues.tags,
    ...existingTool.fieldValues.booleans,
    ...existingTool.fieldValues.users,
    ...existingTool.fieldValues.stringArrays,
    dataOwner: getUserIds(existingTool.fieldValues.users?.dataOwner),
  };
};

const getInitialFormData = (
  existingTool: Tool | undefined,
  toolType: string | undefined
): ToolFormData => ({
  title: existingTool ? existingTool.title : "",
  toolType,
  file: undefined,
  fileName: existingTool ? getToolFileName(existingTool) : "",
  linkUrl: existingTool ? existingTool.webUrl : "",
  adminDisplayName: existingTool?.adminDisplayName ?? "",
  fieldsWithValues: getInitialFields(existingTool),
});

export const ToolForm = (props: ToolFormProps) => {
  const {
    existingTool,
    toolFields,
    onSubmit,
    isLoading: toolLoading,
    isRefetching,
    defaultToolType,
    onToolTypeChange,
    autoOpenPublishDialog,
  } = props;
  const classes = useClasses();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialToolType = getToolType(existingTool, defaultToolType);
  const [toolType, setToolType] = useState<string | undefined>(initialToolType);

  const { instance } = useMsal();
  const currentUser = instance.getActiveAccount();
  const accessLevel = useGetUserAccessLevel();

  const isCurrentUsersDraft = useMemo<boolean>(() => {
    if (!existingTool) {
      return true;
    }

    const userEntraId = currentUser?.idTokenClaims?.oid;

    if (!userEntraId) {
      return false;
    }

    if (userEntraId === existingTool.createdBy?.id) {
      return true;
    }

    return false;
  }, [currentUser, existingTool]);

  const [invalidUrl, setInvalidUrl] = useState<boolean>(false);

  const [duplicateFileNameError, setDuplicateFileNameError] = useState(false);

  const { data: documentNames, isLoading: loadingDocumentNames } =
    useGetCurrentPublishedDocumentsNames();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishOpenSignal, setPublishOpenSignal] = useState(0);
  const hasAutoOpenedPublishDialog = useRef(false);
  const [formDataSourceTool, setFormDataSourceTool] = useState(existingTool);
  const [formData, setFormData] = useState<ToolFormData>(() =>
    getInitialFormData(existingTool, initialToolType)
  );
  const [hasFormAction, setHasFormAction] = useState(false);
  const [toolsSectionOpen, setToolsSectionOpen] = useState<boolean>(true);

  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [cancelCheckedOutDialogOpen, setCancelCheckedOutDialogOpen] =
    useState<boolean>(false);

  const { mutateAsync: cancelToolDraft, isPending: cancelToolDraftPending } =
    useCancelToolDraft(
      existingTool?.versionId || "",
      existingTool?.toolId || ""
    );

  const {
    graphClient,
    isLoading: graphLoading,
    error: graphError,
  } = useGraphClient();

  const { data: chapters } = useChaptersGetAll();

  const kmaVariantChapters = useMemo(() => {
    return chapters?.filter(hasKmaVariantTag) ?? [];
  }, [chapters]);

  const activeField = useMemo<Field | undefined>(() => {
    if (!toolFields || toolFields.length === 0) {
      return;
    }

    const activeField = toolFields?.filter((f) => f.uniqueName === "active");

    return activeField[0];
  }, [toolFields]);

  const archivedField = useMemo<Field | undefined>(() => {
    if (!toolFields || toolFields.length === 0) {
      return;
    }

    const archivedField = toolFields.filter((f) => f.uniqueName === "archived");

    return archivedField[0];
  }, [toolFields]);

  if (existingTool !== formDataSourceTool) {
    const nextToolType = getToolType(existingTool, defaultToolType);
    setFormDataSourceTool(existingTool);
    setToolType(nextToolType);
    setFormData(getInitialFormData(existingTool, nextToolType));
    setHasFormAction(false);
  }

  const selectedFileExtension = getFileExtension(formData.file?.name);
  const existingFileExtension = getFileExtension(existingTool?.webUrl);
  const fileExtension = selectedFileExtension || existingFileExtension;
  const publishAsGeneratedPdfField = toolFields?.find(
    (field) => field.uniqueName === PublishAsGeneratedPdfFieldUniqueName
  );
  const publishAsGeneratedPdfFileNameOrUrl =
    formData.file?.name ?? existingTool?.webUrl;
  const publishAsGeneratedPdfFileType = formData.file
    ? undefined
    : existingTool?.fileType;
  const canPublishCurrentFileAsPdf =
    toolType === ToolTypes.Document &&
    canPublishAsGeneratedPdf(
      publishAsGeneratedPdfFileNameOrUrl,
      publishAsGeneratedPdfFileType
    );
  const publishAsGeneratedPdf = normalizePublishAsGeneratedPdfValue(
    formData.fieldsWithValues?.[
      PublishAsGeneratedPdfFieldUniqueName
    ] as boolean,
    publishAsGeneratedPdfFileNameOrUrl,
    publishAsGeneratedPdfFileType
  );
  const formDataWithDerivedValues = useMemo<ToolFormData>(() => {
    const fieldsWithValues = {
      ...formData.fieldsWithValues,
    };

    if (toolType === ToolTypes.Document && publishAsGeneratedPdfField) {
      fieldsWithValues[PublishAsGeneratedPdfFieldUniqueName] =
        publishAsGeneratedPdf;
    }

    return {
      ...formData,
      toolType,
      fieldsWithValues,
    };
  }, [formData, publishAsGeneratedPdf, publishAsGeneratedPdfField, toolType]);

  const isDuplicateFileName = useCallback((): boolean => {
    if (toolType !== ToolTypes.Document || loadingDocumentNames) {
      return false;
    }

    const fileName = formData.fileName?.trim().toLowerCase();

    if (!fileName) {
      return false;
    }

    const currentDocumentName = existingTool
      ? getToolFileName(existingTool).trim().toLowerCase()
      : null;

    const namesToCheck = documentNames?.filter((docName) => {
      if (currentDocumentName) {
        return docName.trim().toLowerCase() !== currentDocumentName;
      }
      return true;
    });

    return (
      namesToCheck?.some(
        (docName) => docName.trim().toLowerCase() === fileName
      ) ?? false
    );
  }, [
    toolType,
    loadingDocumentNames,
    existingTool,
    formData.fileName,
    documentNames,
  ]);

  const showDuplicateFileNameError =
    duplicateFileNameError ||
    (Boolean(formData.fileName) &&
      !loadingDocumentNames &&
      isDuplicateFileName());

  const handleFileSelect = (file: File) => {
    setHasFormAction(true);
    const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
    setDuplicateFileNameError(false);
    setFormData((prev) => ({
      ...prev,
      file,
      fileName: prev.fileName || fileNameWithoutExtension,
      title: prev.title || fileNameWithoutExtension,
    }));
  };

  const handleFileNameChange = (
    _event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    setHasFormAction(true);
    const value = data.value || "";
    const normalizedValue =
      fileExtension && value.toLowerCase().endsWith(fileExtension.toLowerCase())
        ? value.slice(0, -fileExtension.length)
        : value;

    setDuplicateFileNameError(false);
    setFormData((prev) => ({ ...prev, fileName: normalizedValue }));
  };

  const handleUrlChange = (
    _event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    setHasFormAction(true);
    if (data.value && !isStringAUrl(data.value)) {
      setInvalidUrl(true);
    } else {
      setInvalidUrl(false);
    }

    setFormData((prev) => ({ ...prev, linkUrl: data.value || "" }));
  };

  const handleNameChange = (
    _event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    setHasFormAction(true);
    setFormData((prev) => ({ ...prev, title: data.value || "" }));
  };

  const handleAdminDisplayNameChange = (
    _event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    setHasFormAction(true);
    setFormData((prev) => ({ ...prev, adminDisplayName: data.value || "" }));
  };

  const updateFormDataFields = (newFieldsWithValues: Record<string, any>) => {
    setFormData((formData) => ({
      ...formData,
      fieldsWithValues: newFieldsWithValues,
    }));
  };

  const handleFieldChange = (
    fieldName: string,
    value: FlatVsaaTag[] | boolean | string | string[]
  ): void => {
    setHasFormAction(true);
    const newFieldsWithValues = formData.fieldsWithValues
      ? JSON.parse(JSON.stringify(formData.fieldsWithValues))
      : {};
    newFieldsWithValues[fieldName] = value;

    // When archived is set to true, automatically deactivate
    if (fieldName === "archived" && value === true) {
      newFieldsWithValues["active"] = false;
    }

    updateFormDataFields(newFieldsWithValues);
  };

  const handlePublishAsPdfChange = (
    _ev: ChangeEvent<HTMLInputElement>,
    data: CheckboxOnChangeData
  ) => {
    handleFieldChange(
      PublishAsGeneratedPdfFieldUniqueName,
      normalizePublishAsGeneratedPdfValue(
        data.checked === true,
        publishAsGeneratedPdfFileNameOrUrl,
        publishAsGeneratedPdfFileType
      )
    );
  };

  const submitEnabled = (): boolean => {
    if (toolType === ToolTypes.Document) {
      // Always require a title.
      if (
        !formData.title ||
        !formData.fileName?.trim() ||
        (!existingTool && !formData.file) ||
        isDuplicateFileName()
      ) {
        return false;
      }
    }
    if (
      toolType === ToolTypes.Link &&
      (!formData.linkUrl || !formData.title || invalidUrl)
    ) {
      return false;
    }
    // Check required fields are not empty
    const missingRequiredField = toolFields?.some((field) => {
      const value = formData.fieldsWithValues?.[field.uniqueName];

      if (!field.isRequired) return false;

      if (field.type === 0) {
        // Check if the tag picker field is missing or empty
        return !Array.isArray(value) || value.length === 0;
      }
      return false;
    });

    if (missingRequiredField) {
      return false;
    }

    return hasFormAction;
  };

  const handleSubmit = async (event: any): Promise<void> => {
    event.preventDefault();

    if (isDuplicateFileName()) {
      setDuplicateFileNameError(true);
      return;
    }

    if (!submitEnabled()) {
      return;
    }
    setIsSubmitting(true);

    try {
      await onSubmit(formDataWithDerivedValues);
      setHasFormAction(false);
    } finally {
      setFormData((prev) => ({
        ...prev,
        file: undefined,
      }));
      setIsSubmitting(false);
    }
  };

  const handleSaveAndPublish = async (): Promise<void> => {
    if (!existingTool) {
      if (isDuplicateFileName()) {
        setDuplicateFileNameError(true);
        return;
      }

      if (!submitEnabled()) {
        return;
      }

      setIsSubmitting(true);

      try {
        await onSubmit(formDataWithDerivedValues, { publishAfterSave: true });

        setFormData((prev) => ({
          ...prev,
          file: undefined,
        }));
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    setPublishOpenSignal((signal) => signal + 1);
  };

  const pendingToolUpdate = useMemo(
    () => ({
      title: formData.title,
      toolType,
      linkUrl: formData.linkUrl,
      fileName: formData.fileName,
      adminDisplayName: formData.adminDisplayName,
      fieldsWithValues: validateFormFieldsForRequest(
        formDataWithDerivedValues.fieldsWithValues,
        toolFields ?? []
      ),
      file: formData.file,
    }),
    [formData, formDataWithDerivedValues.fieldsWithValues, toolFields, toolType]
  );

  useEffect(() => {
    if (
      autoOpenPublishDialog &&
      existingTool &&
      !hasAutoOpenedPublishDialog.current
    ) {
      hasAutoOpenedPublishDialog.current = true;
      setPublishOpenSignal((signal) => signal + 1);
    }
  }, [autoOpenPublishDialog, existingTool]);

  useUnsavedChanges(
    submitEnabled() && !isSubmitting && !isPublishing,
    t("unsavedChangesMessage")
  );

  // Stops Enter action from clearing other fields.
  const preventEnter = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };
  const handleCancelFileSelection = () => {
    setHasFormAction(true);
    setFormData((prev) => ({
      ...prev,
      file: undefined,
    }));
  };

  const handleCancelDraftClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelDraftConfirm = async () => {
    if (!isCurrentUsersDraft) {
      setCancelDialogOpen(false);
      setCancelCheckedOutDialogOpen(true);
      return;
    }

    if (existingTool) {
      try {
        await cancelToolDraft();
        setCancelDialogOpen(false);
        if (toolType === ToolTypes.Document) {
          navigate(ClientRoutes.Documents);
        } else if (toolType === ToolTypes.Link) {
          navigate(ClientRoutes.Links);
        } else {
          navigate(ClientRoutes.Documents + "?view=drafts");
        }
      } catch (error) {
        console.error("Error canceling tool draft:", error);
      }
    }
  };

  const handleCancelDraftCancel = () => {
    setCancelDialogOpen(false);
  };

  const handleCancelCheckedOutDraftConfirm = async () => {
    if (existingTool) {
      try {
        await cancelToolDraft();
        setCancelCheckedOutDialogOpen(false);
        if (toolType === ToolTypes.Document) {
          navigate(ClientRoutes.Documents);
        } else if (toolType === ToolTypes.Link) {
          navigate(ClientRoutes.Links);
        } else {
          navigate(ClientRoutes.Documents + "?view=drafts");
        }
      } catch (error) {
        console.error("Error canceling tool draft:", error);
      }
    }
  };

  const handleCancelCheckedOutDraftCancel = () => {
    setCancelCheckedOutDialogOpen(false);
  };

  const renderFileNameField = () => (
    <>
      <Label weight="semibold">{t("fileName")}</Label>
      <div className={classes.fileNameInputContainer}>
        <Input
          value={formData.fileName ?? ""}
          onChange={handleFileNameChange}
          placeholder={t("enterFileName")}
          className={classes.textFieldWidth}
          onKeyDown={preventEnter}
          disabled={!isCurrentUsersDraft || accessLevel < 3}
        />
        {fileExtension && (
          <span
            className={classes.fileExtensionBadge}
            aria-disabled="true"
            tabIndex={-1}
          >
            {fileExtension}
          </span>
        )}
      </div>
      {showDuplicateFileNameError && (
        <span className={classes.warningText}>
          {t("duplicateFileNameError")}
        </span>
      )}
    </>
  );

  const renderPublishAsGeneratedPdfField = () => {
    if (
      accessLevel < 3 ||
      !publishAsGeneratedPdfField ||
      toolType !== ToolTypes.Document
    ) {
      return null;
    }

    return (
      <Checkbox
        checked={publishAsGeneratedPdf}
        disabled={!isCurrentUsersDraft || !canPublishCurrentFileAsPdf}
        onChange={handlePublishAsPdfChange}
        label={t("publishAsPdfCheckboxLabel")}
        labelPosition="after"
      />
    );
  };

  const renderDocumentSpecificFields = () => {
    if (existingTool) {
      return (
        <>
          <Label weight="semibold">{t("title")}</Label>
          <Input
            value={formData.title}
            onChange={handleNameChange}
            placeholder={t("giveToolTitle")}
            className={classes.textFieldWidth}
            onKeyDown={preventEnter}
            disabled={!isCurrentUsersDraft || accessLevel < 3}
          />
          {renderFileNameField()}
          <div className={classes.existingToolInformation}>
            <Label weight="semibold">{t("document")}</Label>
            <ToolLink
              tool={existingTool}
              readonly={!isCurrentUsersDraft}
              showDownloadButton
              isDraft={true}
            />
          </div>
          {isCurrentUsersDraft && accessLevel >= 3 && (
            <div>
              <FileSelection
                header={t("changeDocument") + "?"}
                onFileSelect={handleFileSelect}
                selectedFileName={formData.file?.name}
              />
              {formData.file && (
                <div className={classes.fileSelectionContainer}>
                  <p>
                    {t("newSelectedFile")}:{" "}
                    <strong>{formData.file.name}</strong>
                  </p>
                  <Button
                    appearance="secondary"
                    onClick={handleCancelFileSelection}
                    className={classes.confirmDeleteButton}
                  >
                    {t("cancelSelectedNewFile")}
                  </Button>
                </div>
              )}
            </div>
          )}
          {renderPublishAsGeneratedPdfField()}
        </>
      );
    }

    return (
      <>
        <Label weight="semibold">{t("title")}</Label>
        <Input
          value={formData.title}
          onChange={handleNameChange}
          placeholder={t("giveToolTitle")}
          className={classes.textFieldWidth}
          onKeyDown={preventEnter}
          disabled={!isCurrentUsersDraft}
        />
        {renderFileNameField()}
        <FileSelection
          header={t("chooseFile")}
          onFileSelect={handleFileSelect}
          selectedFileName={formData.file?.name}
        />
        {renderPublishAsGeneratedPdfField()}
      </>
    );
  };

  const renderLinkSpecificFields = () => {
    return (
      <>
        <FluentField
          label={{
            children: t("adminDisplayNameLabel"),
            weight: "semibold",
          }}
          hint={t("adminDisplayNameHint")}
        >
          <Input
            value={formData.adminDisplayName ?? ""}
            onChange={handleAdminDisplayNameChange}
            className={classes.textFieldWidth}
            onKeyDown={preventEnter}
            disabled={!isCurrentUsersDraft}
          />
        </FluentField>
        <Label weight="semibold">{t("title")}</Label>
        <Input
          value={formData.title}
          onChange={handleNameChange}
          placeholder={t("giveToolTitle")}
          className={classes.textFieldWidth}
          onKeyDown={preventEnter}
          disabled={!isCurrentUsersDraft}
        />
        <Label weight="semibold">{t("url")}</Label>
        <Input
          value={formData.linkUrl}
          onChange={handleUrlChange}
          placeholder={t("enterToolUrl")}
          className={classes.textFieldWidth}
          onKeyDown={preventEnter}
          type="url"
          disabled={!isCurrentUsersDraft}
        />
        {invalidUrl && (
          <span className={classes.warningText}>{t("invalidUrlErrorMsg")}</span>
        )}
      </>
    );
  };

  return (
    <div className={classes.root}>
      {!isCurrentUsersDraft && existingTool && existingTool.createdBy && (
        <StickyTopBannerStack>
          <CheckoutMessageBar checkedOutTo={existingTool.createdBy} />
        </StickyTopBannerStack>
      )}
      <form onSubmit={handleSubmit}>
        {!existingTool && !toolType && accessLevel >= 3 && (
          <div className={classes.newToolContainerPrompt}>
            <Button
              appearance="primary"
              onClick={() => {
                setHasFormAction(true);
                setToolType(ToolTypes.Document);
                setFormData((prev) => ({
                  ...prev,
                  toolType: ToolTypes.Document,
                }));
                onToolTypeChange?.(ToolTypes.Document);
              }}
            >
              {t("createDocumentTool")}
            </Button>
            <Button
              appearance="primary"
              onClick={() => {
                setHasFormAction(true);
                setToolType(ToolTypes.Link);
                setFormData((prev) => ({
                  ...prev,
                  toolType: ToolTypes.Link,
                }));
                onToolTypeChange?.(ToolTypes.Link);
              }}
            >
              {t("createLinkTool")}
            </Button>
          </div>
        )}
        {toolType && (
          <div className={classes.newToolContainer}>
            {toolType === ToolTypes.Document
              ? renderDocumentSpecificFields()
              : renderLinkSpecificFields()}

            <div className={classes.toolStatusContainer}>
              {activeField && (
                <div className={classes.checkboxContainer}>
                  <Switch
                    checked={
                      formData.fieldsWithValues?.[activeField.uniqueName] !==
                      undefined
                        ? (formData.fieldsWithValues?.[
                            activeField.uniqueName
                          ] as boolean)
                        : (activeField.defaultValue as boolean)
                    }
                    label={activeField.displayName}
                    disabled={
                      !isCurrentUsersDraft ||
                      (formData.fieldsWithValues?.["archived"] as boolean) ===
                        true
                    }
                    onChange={(
                      _e: ChangeEvent<HTMLInputElement>,
                      data: CheckboxOnChangeData
                    ) =>
                      handleFieldChange(
                        activeField.uniqueName,
                        data.checked ?? false
                      )
                    }
                  />
                </div>
              )}
              {archivedField && (
                <div className={classes.toggleContainer}>
                  <Checkbox
                    checked={
                      formData.fieldsWithValues?.[archivedField.uniqueName] !==
                      undefined
                        ? (formData.fieldsWithValues?.[
                            archivedField.uniqueName
                          ] as boolean)
                        : (archivedField.defaultValue as boolean)
                    }
                    onChange={(
                      _ev: ChangeEvent<HTMLInputElement>,
                      data: CheckboxOnChangeData
                    ) =>
                      handleFieldChange(
                        archivedField.uniqueName,
                        data.checked ?? false
                      )
                    }
                    label={archivedField.displayName}
                    labelPosition="after"
                    disabled={!isCurrentUsersDraft}
                  />
                </div>
              )}
            </div>
            {accessLevel >= 3 && (
              <Accordion collapsible openItems={toolsSectionOpen ? ["1"] : []}>
                <AccordionItem value="1">
                  <AccordionHeader
                    onClick={() => setToolsSectionOpen(!toolsSectionOpen)}
                    className={classes.accordionHeader}
                    size="extra-large"
                    expandIconPosition="end"
                    expandIcon={
                      toolsSectionOpen ? (
                        <ChevronDownFilled />
                      ) : (
                        <ChevronRightFilled />
                      )
                    }
                  >
                    {t("toolSectionTags")}
                  </AccordionHeader>
                  <AccordionPanel>
                    {toolFields?.map((validField) => {
                      if (
                        validField.type === NumFieldType.TagPicker &&
                        validField.tagCollectionId
                      ) {
                        if (
                          validField.uniqueName === KmaChapterFieldUniqueName
                        ) {
                          // Rendered separately as a chapter multi-select below
                          return <Fragment key={validField.uniqueName} />;
                        }
                        if (
                          validField.uniqueName ===
                          PublishAsGeneratedPdfFieldUniqueName
                        ) {
                          return <Fragment key={validField.uniqueName} />;
                        }
                        if (
                          validField.uniqueName === "improvementLeader" &&
                          toolType !== ToolTypes.Document
                        ) {
                          return <Fragment key={validField.uniqueName} />;
                        }
                        return (
                          <div
                            key={validField.uniqueName}
                            className={classes.formSpacingContainer}
                          >
                            {isCurrentUsersDraft ? (
                              <VsaaTagPicker
                                tagCollectionId={validField.tagCollectionId}
                                label={validField.displayName}
                                onChange={(tagValue) =>
                                  handleFieldChange(
                                    validField.uniqueName,
                                    tagValue
                                  )
                                }
                                value={
                                  formData.fieldsWithValues?.[
                                    validField.uniqueName
                                  ] as VsaaTag[]
                                }
                                multiValue={validField.multiValue}
                                isRequired={validField.isRequired}
                              />
                            ) : (
                              <div>
                                <Label>{validField.displayName}</Label>
                                <DisplayField
                                  type={FieldTypes.Tag}
                                  label={validField.displayName}
                                  value={
                                    formData.fieldsWithValues?.[
                                      validField.uniqueName
                                    ] as VsaaTag[]
                                  }
                                  showPath={validField.uniqueName === "process"}
                                />
                              </div>
                            )}
                          </div>
                        );
                      } else if (
                        validField.type === NumFieldType.PeoplePicker &&
                        toolType === ToolTypes.Document
                      ) {
                        return toolLoading ? (
                          <Spinner key={validField.uniqueName} />
                        ) : (
                          <div
                            className={classes.peoplePickerContainer}
                            key={validField.uniqueName}
                          >
                            {isCurrentUsersDraft ? (
                              <>
                                <Label>{validField.displayName}</Label>
                                {graphError && (
                                  <p>
                                    {t("graphAuthenticationError")}:{" "}
                                    {graphError}
                                  </p>
                                )}
                                {graphClient ? (
                                  <FluentPeoplePicker
                                    key={
                                      getUserIds(
                                        formData.fieldsWithValues?.[
                                          validField.uniqueName
                                        ] as string[]
                                      ).join(",") || "empty"
                                    }
                                    graphClient={graphClient}
                                    groupId={
                                      import.meta.env.VITE_USER_SEARCH_GROUP_ID
                                    }
                                    placeholder={t("searchName")}
                                    multiSelect={false}
                                    defaultSelectedUserIds={getUserIds(
                                      formData.fieldsWithValues?.[
                                        validField.uniqueName
                                      ] as string[]
                                    )}
                                    hideWhenSelected={true}
                                    onChange={(users: UserInformation[]) => {
                                      const userIds = users
                                        .map((u) => u.id)
                                        .filter(Boolean) as string[];
                                      handleFieldChange(
                                        validField.uniqueName,
                                        userIds
                                      );
                                    }}
                                  />
                                ) : graphLoading ? (
                                  <Spinner size="tiny" />
                                ) : (
                                  <p>{t("graphClientNotAvailable")}</p>
                                )}
                              </>
                            ) : (
                              <div className={classes.userFieldReadOnlyWrapper}>
                                <Label>{validField.displayName}</Label>
                                <DisplayField
                                  type={FieldTypes.User}
                                  label={validField.displayName}
                                  value={
                                    existingTool?.fieldValues?.users[
                                      validField.uniqueName
                                    ]
                                  }
                                />
                              </div>
                            )}
                          </div>
                        );
                      } else if (
                        validField.type === NumFieldType.Checkbox &&
                        validField.uniqueName !== "archived" &&
                        validField.uniqueName !==
                          PublishAsGeneratedPdfFieldUniqueName
                      ) {
                        return (
                          <Checkbox
                            key={validField.uniqueName}
                            checked={
                              formData.fieldsWithValues?.[
                                validField.uniqueName
                              ] !== undefined
                                ? (formData.fieldsWithValues?.[
                                    validField.uniqueName
                                  ] as boolean)
                                : (validField.defaultValue as boolean)
                            }
                            disabled={!isCurrentUsersDraft}
                            onChange={(
                              _ev: ChangeEvent<HTMLInputElement>,
                              data: CheckboxOnChangeData
                            ) =>
                              handleFieldChange(
                                validField.uniqueName,
                                data.checked ?? false
                              )
                            }
                            label={validField.displayName}
                            labelPosition="after"
                          />
                        );
                      }

                      return null;
                    })}

                    {accessLevel >= 3 &&
                      (toolType === ToolTypes.Link ||
                        toolType === ToolTypes.Document) && (
                        <div className={classes.formSpacingContainer}>
                          <p>
                            <Label weight="semibold">
                              {t("kmaChapterLabel")}
                            </Label>
                          </p>
                          {isCurrentUsersDraft ? (
                            <Dropdown
                              multiselect
                              selectedOptions={
                                (formData.fieldsWithValues?.[
                                  KmaChapterFieldUniqueName
                                ] as string[]) ?? []
                              }
                              value={
                                chapters
                                  ?.filter((c) =>
                                    (
                                      (formData.fieldsWithValues?.[
                                        KmaChapterFieldUniqueName
                                      ] as string[]) ?? []
                                    ).includes(c.id)
                                  )
                                  .map((c) => c.title)
                                  .join(", ") ?? ""
                              }
                              onOptionSelect={(_e, data) =>
                                handleFieldChange(
                                  KmaChapterFieldUniqueName,
                                  data.selectedOptions
                                )
                              }
                              placeholder={t("selectChapters")}
                            >
                              {kmaVariantChapters.map((chapter) => (
                                <Option key={chapter.id} value={chapter.id}>
                                  {chapter.title}
                                </Option>
                              ))}
                            </Dropdown>
                          ) : (
                            <span>
                              {(
                                chapters
                                  ?.filter((c) =>
                                    (
                                      (formData.fieldsWithValues?.[
                                        KmaChapterFieldUniqueName
                                      ] as string[]) ?? []
                                    ).includes(c.id)
                                  )
                                  .map((c) => c.title) ?? []
                              ).join(", ") || "—"}
                            </span>
                          )}
                        </div>
                      )}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}

            {isCurrentUsersDraft && accessLevel >= 3 ? (
              <>
                <div className={classes.actionButtons}>
                  <SaveSplitButton
                    size="large"
                    saveText={existingTool ? t("save") : t("createTool")}
                    onSave={handleSubmit}
                    saveDisabled={!submitEnabled()}
                    isSaveLoading={toolLoading || isRefetching}
                    showSaveAndPublishOption={
                      (toolType === ToolTypes.Link || accessLevel >= 3) &&
                      Boolean(existingTool) &&
                      submitEnabled() &&
                      !isSubmitting &&
                      !toolLoading &&
                      !isRefetching
                    }
                    saveAndPublishText={t("saveAndPublish")}
                    onSaveAndPublish={handleSaveAndPublish}
                    saveAndPublishDisabled={
                      !submitEnabled() || toolLoading || isRefetching
                    }
                  />
                  {/** Publish only if we’re editing an existing draft **/}
                  {existingTool &&
                    (toolType === ToolTypes.Link || accessLevel >= 3) && (
                      <ToolPublishDialog
                        versionId={existingTool.versionId || ""}
                        toolId={existingTool.toolId}
                        tooltype={
                          existingTool.fileUniqueId
                            ? ToolTypes.Document
                            : ToolTypes.Link
                        }
                        openSignal={publishOpenSignal}
                        onPublishStart={() => setIsPublishing(true)}
                        onPublishFailed={() => setIsPublishing(false)}
                        pendingUpdate={pendingToolUpdate}
                        disabled={
                          submitEnabled() ||
                          showDuplicateFileNameError ||
                          invalidUrl ||
                          toolLoading ||
                          isRefetching
                        } // disable if there are unsaved edits or a save is in progress
                      />
                    )}
                </div>
                {accessLevel >= 3 && existingTool && (
                  <div className={classes.deleteButton}>
                    <Button
                      appearance="secondary"
                      onClick={handleCancelDraftClick}
                    >
                      {t("cancelDraft")}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                {accessLevel >= 3 && existingTool && (
                  <div className={classes.actionButtons}>
                    <ToolPublishDialog
                      versionId={existingTool.versionId || ""}
                      toolId={existingTool.toolId}
                      tooltype={
                        existingTool.fileUniqueId
                          ? ToolTypes.Document
                          : ToolTypes.Link
                      }
                      openSignal={publishOpenSignal}
                      onPublishStart={() => setIsPublishing(true)}
                      onPublishFailed={() => setIsPublishing(false)}
                      pendingUpdate={pendingToolUpdate}
                      disabled={
                        submitEnabled() ||
                        showDuplicateFileNameError ||
                        invalidUrl ||
                        toolLoading ||
                        isRefetching
                      }
                      onlyPublish={true}
                    />
                    <Button
                      appearance="secondary"
                      onClick={handleCancelDraftClick}
                    >
                      {t("cancelDraft")}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </form>

      {accessLevel >= 3 && existingTool && (
        <CancelToolDraftDialog
          isOpen={cancelDialogOpen}
          onCancel={handleCancelDraftCancel}
          onConfirm={handleCancelDraftConfirm}
          isLoading={cancelToolDraftPending}
          isWarning={!isCurrentUsersDraft}
        />
      )}

      {accessLevel >= 3 && existingTool && (
        <CancelToolDraftDialog
          isOpen={cancelCheckedOutDialogOpen}
          onCancel={handleCancelCheckedOutDraftCancel}
          onConfirm={handleCancelCheckedOutDraftConfirm}
          isLoading={cancelToolDraftPending}
          isWarning
          title={t("cancelDraftDialogCheckedOutTitle")}
          message={t("cancelDraftDialogCheckedOutMessage", {
            user:
              existingTool.createdBy?.displayName ||
              existingTool.createdBy?.email ||
              t("unknownUser"),
          })}
        />
      )}
    </div>
  );
};

export default ToolForm;
